<template>
  <div class="voice-room min-h-screen bg-gray-900 text-white flex flex-col">
    <LoadingSpinner :isLoading="isLoading" />

    <!-- Header -->
    <div class="bg-gray-800 px-4 py-3 flex items-center justify-between border-b border-gray-700">
      <div class="flex items-center">
        <span class="inline-block w-3 h-3 rounded-full mr-2" :class="callStatus === 'connected' ? 'bg-green-400' : callStatus === 'connecting' ? 'bg-yellow-400 animate-pulse' : 'bg-red-400'"></span>
        <span class="font-semibold text-sm uppercase tracking-wide">SecurArc – Voice Room</span>
      </div>
      <div class="text-xs text-gray-400" v-if="alarm">
        Alarm: <span class="text-white font-medium">{{ alarm.unit_name || alarm.phone }}</span>
      </div>
    </div>

    <!-- Main content -->
    <div class="flex-1 flex flex-col lg:flex-row">

      <!-- Left: Alarm Info Panel -->
      <div class="lg:w-1/3 bg-gray-800 p-5 border-r border-gray-700">
        <h2 class="text-lg font-semibold font-serif mb-4 text-primary-300">Alarm Information</h2>

        <div v-if="alarm" class="space-y-3 text-sm">
          <div class="flex justify-between border-b border-gray-700 pb-2">
            <span class="text-gray-400">Device</span>
            <span class="font-medium">{{ alarm.unit_name }}</span>
          </div>
          <div class="flex justify-between border-b border-gray-700 pb-2">
            <span class="text-gray-400">Phone</span>
            <span class="font-medium">{{ alarm.phone }}</span>
          </div>
          <div class="flex justify-between border-b border-gray-700 pb-2">
            <span class="text-gray-400">Model</span>
            <span class="font-medium">{{ alarm.modelnumber }}</span>
          </div>
          <div class="flex justify-between border-b border-gray-700 pb-2">
            <span class="text-gray-400">IMEI</span>
            <span class="font-medium">{{ alarm.imei_number }}</span>
          </div>
          <div class="flex justify-between border-b border-gray-700 pb-2">
            <span class="text-gray-400">Alarm Type</span>
            <span class="font-medium uppercase">{{ alarm.type }}</span>
          </div>
        </div>
        <div v-else class="text-gray-500 text-sm">Loading alarm info...</div>

        <!-- GPS tracking link -->
        <div class="mt-6" v-if="alarm && alarm_center_id">
          <router-link
            :to="'/alarm-center/securtrack/' + alarm_center_id + '/' + alarm_id"
            class="btn-blue-outline w-full text-center block"
            target="_blank"
          >
            <BaseIcon icon="map-marker-alt" class="mr-1" /> Open GPS Track
          </router-link>
        </div>
      </div>

      <!-- Right: Voice Room Panel -->
      <div class="flex-1 flex flex-col items-center justify-center p-6">

        <!-- Status Message -->
        <div class="text-center mb-8">
          <div v-if="callStatus === 'idle'" class="text-gray-400">
            <BaseIcon icon="phone" class="text-5xl mb-3 text-gray-500" />
            <p class="text-lg">Press "Join Audio" to connect to the alarm room.</p>
          </div>
          <div v-else-if="callStatus === 'connecting'" class="text-yellow-400">
            <BaseIcon icon="spinner" spin class="text-5xl mb-3" />
            <p class="text-lg">Connecting to voice room...</p>
          </div>
          <div v-else-if="callStatus === 'connected'" class="text-green-400">
            <BaseIcon icon="microphone" class="text-5xl mb-3" />
            <p class="text-lg font-semibold">Live – You are connected</p>
            <p class="text-sm text-gray-400 mt-1">Room: <span class="font-mono text-xs">{{ roomName }}</span></p>
          </div>
          <div v-else-if="callStatus === 'ended'" class="text-gray-500">
            <BaseIcon icon="phone-slash" class="text-5xl mb-3" />
            <p class="text-lg">Call ended.</p>
          </div>
          <div v-else-if="callStatus === 'error'" class="text-red-400">
            <BaseIcon icon="exclamation-triangle" class="text-5xl mb-3" />
            <p class="text-lg">Failed to connect. Please try again.</p>
            <p class="text-xs text-gray-500 mt-1">{{ errorMsg }}</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-4">
          <button
            v-if="callStatus === 'idle' || callStatus === 'ended' || callStatus === 'error'"
            class="btn-green px-8 py-3 text-lg"
            @click.prevent="joinRoom"
            :disabled="isLoading"
          >
            <BaseIcon icon="phone" class="mr-2" /> Join Audio
          </button>

          <button
            v-if="callStatus === 'connected'"
            :class="isMuted ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'"
            class="text-white font-semibold px-8 py-3 rounded text-lg transition duration-200"
            @click.prevent="toggleMute"
          >
            <BaseIcon :icon="isMuted ? 'microphone-slash' : 'microphone'" class="mr-2" />
            {{ isMuted ? 'Unmute' : 'Mute' }}
          </button>

          <button
            v-if="callStatus === 'connected' || callStatus === 'connecting'"
            class="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded text-lg transition duration-200"
            @click.prevent="leaveRoom"
          >
            <BaseIcon icon="phone-slash" class="mr-2" /> Leave Room
          </button>
        </div>

        <!-- Jitsi iFrame container: visibility:hidden keeps it fully rendered in the DOM
             with real dimensions (400x300). This is required so the browser allows
             audio OUTPUT (remote participants) — a 1px or off-screen iFrame causes
             the browser to block remote audio playback even if mic input works fine. -->
        <div id="jitsi-container" style="position:absolute;left:-9999px;top:-9999px;width:400px;height:300px;visibility:hidden;pointer-events:none;" aria-hidden="true"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VoiceRoom",
  data() {
    return {
      isLoading: false,
      alarm_id: this.$route.params.alarm_id,
      alarm_center_id: this.$route.params.id,
      alarm: null,
      roomName: null,
      jitsiDomain: null,
      jwtToken: null,
      jitsiApi: null,
      callStatus: "idle", // idle | connecting | connected | ended | error
      errorMsg: null,
      isMuted: true,
    };
  },

  methods: {
    async fetchAlarmInfo() {
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_SERVER_URL}/alarm/${this.alarm_id}`
        );
        this.alarm = response.data.data || response.data;
      } catch (error) {
        this.handleError(error);
      }
    },

    async fetchVoiceToken() {
      const response = await this.axios.get(
        `${process.env.VUE_APP_SERVER_URL}/alarms/${this.alarm_id}/operator-voice-token`
      );
      return response.data.data;
    },

    async joinRoom() {
      try {
        this.callStatus = "connecting";
        this.isLoading = true;
        this.errorMsg = null;

        const tokenData = await this.fetchVoiceToken();
        this.roomName = tokenData.roomName;
        this.jwtToken = tokenData.jwt;
        this.jitsiDomain = tokenData.domain;

        this.isLoading = false;
        this.loadJitsiScript();
      } catch (error) {
        this.isLoading = false;
        this.callStatus = "error";
        this.errorMsg = error?.response?.data?.message || "Could not retrieve voice token.";
      }
    },

    loadJitsiScript() {
      // Load the Jitsi External API script dynamically if not already loaded
      if (window.JitsiMeetExternalAPI) {
        this.initJitsi();
        return;
      }
      const script = document.createElement("script");
      script.src = `https://${this.jitsiDomain}/external_api.js`;
      script.onload = () => this.initJitsi();
      script.onerror = () => {
        this.callStatus = "error";
        this.errorMsg = "Could not load Jitsi library from server.";
      };
      document.head.appendChild(script);
    },

    initJitsi() {
      const container = document.getElementById("jitsi-container");

      this.jitsiApi = new window.JitsiMeetExternalAPI(this.jitsiDomain, {
        roomName: this.roomName,
        jwt: this.jwtToken,
        width: 400,
        height: 300,
        parentNode: container,
        configOverwrite: {
          startWithAudioMuted: true,
          startWithVideoMuted: true,
          startAudioOnly: true,         // Audio-only mode — no video track at all
          disableDeepLinking: true,
          prejoinPageEnabled: false,
        },
        interfaceConfigOverwrite: {
          filmStripOnly: false,
          TOOLBAR_BUTTONS: [], // No toolbar shown in hidden container
        },
      });

      this.jitsiApi.addEventListener("videoConferenceJoined", () => {
        this.callStatus = "connected";
        this.isMuted = true;
        // Force-mute the mic regardless of server config.
        // startWithAudioMuted in configOverwrite is unreliable on self-hosted Jitsi.
        this.jitsiApi.isAudioMuted().then((muted) => {
          if (!muted) {
            this.jitsiApi.executeCommand("toggleAudio");
          }
        });
      });

      this.jitsiApi.addEventListener("videoConferenceLeft", () => {
        this.callStatus = "ended";
        this.jitsiApi = null;
      });

      this.jitsiApi.addEventListener("audioAvailabilityChanged", (event) => {
        // If browser blocks audio (e.g. autoplay policy), log a clear error for debugging
        if (!event.available) {
          console.warn("[VoiceRoom] Audio became unavailable — browser may be blocking audio output.");
        }
      });

      this.jitsiApi.addEventListener("errorOccurred", (event) => {
        this.callStatus = "error";
        this.errorMsg = event.error?.message || "Unknown Jitsi error.";
      });
    },

    toggleMute() {
      if (this.jitsiApi) {
        this.jitsiApi.executeCommand("toggleAudio");
        // Small delay to let Jitsi apply the toggle before reading the state
        setTimeout(() => {
          this.jitsiApi && this.jitsiApi.isAudioMuted().then((muted) => {
            this.isMuted = muted;
          });
        }, 300);
      }
    },

    leaveRoom() {
      if (this.jitsiApi) {
        this.jitsiApi.executeCommand("hangup");
        this.jitsiApi.dispose();
        this.jitsiApi = null;
      }
      this.callStatus = "ended";
    },
  },

  created() {
    this.fetchAlarmInfo();
  },

  beforeDestroy() {
    // Ensure Jitsi is cleaned up if the operator navigates away
    if (this.jitsiApi) {
      this.jitsiApi.dispose();
    }
  },
};
</script>
